class ProductVisualV2Gallery {
    constructor(container, options) {
        this.container = container instanceof Element ? container : document.querySelector(container);
        this.wrapper = this.container.querySelector(".productVisualV2Wrapper");
        this.slides = this.wrapper.querySelectorAll(".productVisualV2Slide") ? Array.from(this.wrapper.querySelectorAll(".productVisualV2Slide")) : [];
        this.pagination = this.container.querySelector(".productVisualV2Pagination");
        this.navPrev = this.container.querySelector(".productVisualV2Nav.prev");
        this.navNext = this.container.querySelector(".productVisualV2Nav.next");
        this.lightbox = null;

        // Setup lightbox
        if (options.lightbox) {
            this.setupLightbox(options);
        }

        // Init
        this.updateNavigation();
        this.updatePagination();

        // Hydrate
        this.wrapper.addEventListener("scrollend", () => {
            this.updateNavigation();
            this.updatePagination();
        });
        this.navPrev?.addEventListener("click", () => {
            this.prev();
        });
        this.navNext?.addEventListener("click", () => {
            this.next();
        });
        this.pagination?.addEventListener("click", (e) => {
            this.paginate(e);
        });


        window.addEventListener("resize", (e) => {
            this.updateNavigation();
            this.updatePagination();
        });
    }

    /***
     * Set up the lightbox
     * @param options {Object}
     *
     * @returns {Element}
     */
    setupLightbox(options) {
        const lightboxEl = options.lightbox instanceof Element ? options.lightbox : document.querySelector(options.lightbox);
        this.lightbox = {
            dialog: lightboxEl,
            zoomIn: lightboxEl.querySelector(".productVisualV2LightboxActionsZoomInButton"),
            zoomOut: lightboxEl.querySelector(".productVisualV2LightboxActionsZoomOutButton"),
            swiper: new Swiper(lightboxEl.querySelector(".swiper"), {
                centeredSlides: true,
                slidesPerView: 1,
                navigation: {
                    prevEl: ".productVisualV2LightboxSwiperPrev",
                    nextEl: ".productVisualV2LightboxSwiperNext"
                }
            }),
        }

        // Slide Change
        this.lightbox.swiper.on("slideChange", (swiper) => {
            if (this.lightbox.swiper.zoom.enabled) {
                this.lightbox.swiper.zoom.out();
            }

            // A video keeps playing off-screen unless it is stopped on the way out
            this.pauseVideos();

            // Reset Zoom Buttons State
            this.lightbox.zoomIn.removeAttribute("disabled");
            this.lightbox.zoomOut.setAttribute("disabled", "true");
            this.updateZoomButtons();

            // Thumbnails
            this.lightbox.dialog.querySelector(".productVisualV2LightboxThumbnailsItem.active").classList.remove("active");
            this.lightbox.dialog.querySelector(`.productVisualV2LightboxThumbnailsItem[data-index="${swiper.realIndex}"]`).classList.add("active");
        });

        // Open lightbox on slide click
        for (const slide of this.slides) {
            slide.addEventListener("click", () => {
                this.lightbox.swiper.slideTo(parseInt(slide.dataset.index));
                this.openLightbox();
            });
        }

        // Close lightbox on close button click
        this.lightbox.dialog.querySelector(".productVisualV2LightboxActionsCloseButton").addEventListener("click", () => {
            this.closeLightbox();
        });

        // The dialog also closes on esc without going through closeLightbox, so clean up on the native event instead
        this.lightbox.dialog.addEventListener("close", () => {
            this.pauseVideos();
            document.body.classList.remove("noScroll");
        });

        // Zoom In
        this.lightbox.zoomIn.addEventListener("click", () => {
            this.lightbox.swiper.zoom.enable();
            this.lightbox.swiper.zoom.in(1.5);
            this.lightbox.zoomOut.removeAttribute("disabled");
            this.lightbox.zoomIn.setAttribute("disabled", "true");
        });

        // Zoom Out
        this.lightbox.zoomOut.addEventListener("click", () => {
            this.lightbox.swiper.zoom.out();
            this.lightbox.swiper.zoom.disable();
            this.lightbox.zoomIn.removeAttribute("disabled");
            this.lightbox.zoomOut.setAttribute("disabled", "true");
        });

        // Thumbnails
        for (const thumbnail of this.lightbox.dialog.querySelectorAll(".productVisualV2LightboxThumbnailsItem")) {
            const classThisContent = this;
            thumbnail.addEventListener("click", function (e) {
                classThisContent.lightbox.swiper.slideTo(parseInt(this.dataset.index));
                classThisContent.lightbox.dialog.querySelector(".productVisualV2LightboxThumbnailsItem.active").classList.remove("active");
                this.classList.add("active");
            });
        }

        // Match the controls to whichever slide the swiper starts on
        this.updateZoomButtons();
    }

    /***
     * Shows the zoom controls only while an image slide is active, since zoom and video cannot share a slide
     *
     * @returns void
     */
    updateZoomButtons() {
        const activeSlide = this.lightbox.swiper.slides[this.lightbox.swiper.activeIndex];
        const isImage = activeSlide?.dataset.mediaType !== "video";

        this.lightbox.zoomIn.classList.toggle("active", isImage);
        this.lightbox.zoomOut.classList.toggle("active", isImage);
    }

    /***
     * Stops every video in the lightbox
     *
     * @returns void
     */
    pauseVideos() {
        this.lightbox.dialog.querySelectorAll(".productVisualV2LightboxSwiperSlideVideo").forEach(video => {
            video.pause();
        });
    }

    /***
     * Get the Active Slide
     *
     * @returns {Element}
     */
    getActiveSlide() {
        const wrapperRect = this.getBoundingClientRect(this.wrapper);
        const slides = this.slides;

        return slides
            .find(slide => {
                const slideRect = this.getBoundingClientRect(slide);
                return Object.keys(slideRect).every(key => {
                    return slideRect[key] === wrapperRect[key];
                });
            });
    }

    /***
     * Utility method to get the bounding client rect of an element
     * @param element {Element}
     *
     * @returns {Object}
     */
    getBoundingClientRect(element) {
        const rect = element.getBoundingClientRect();
        return {
            top: rect.top,
            right: rect.right,
            bottom: rect.bottom,
            left: rect.left,
            width: rect.width,
            height: rect.height,
            x: rect.x,
            y: rect.y
        };
    }

    /***
     * Updates the pagination
     *
     * @returns void
     */
    updatePagination() {
        if (!this.pagination || !this.getActiveSlide()) {
            return;
        }

        const paginationItems = this.pagination.querySelectorAll(".productVisualV2PaginationListItem");

        for (const paginationItem of paginationItems) {
            paginationItem.classList.toggle("active", parseInt(paginationItem.dataset.index) <= parseInt(this.getActiveSlide().dataset.index));
        }
    }

    /***
     * Updates the navigation
     *
     * @returns void
     */
    updateNavigation() {
        if (!this.navPrev || !this.navNext || !this.getActiveSlide()) {
            return;
        }

        const totalSlides = this.slides.length;
        const currentIndex = parseInt(this.getActiveSlide().dataset.index);

        this.navPrev.classList.toggle("active", currentIndex > 0);
        this.navNext.classList.toggle("active", currentIndex !== totalSlides - 1);
    }

    /***
     * Scrolls the slider to provided index
     * @param index {int}
     *
     * @returns void
     */
    scrollTo(index) {
        const scrollAmount = this.slides
            .filter(slide => {
                return parseInt(slide.dataset.index) < index
            })
            .reduce((prev, current) => {
                return prev + current.offsetWidth;
            }, 0);

        this.wrapper.scrollTo({left: scrollAmount});
    }

    /***
     * Handles Pagination
     *
     * @returns void
     */
    paginate(event) {
        if (!event.target.classList.contains("productVisualV2PaginationListItem")) {
            return;
        }

        this.scrollTo(parseInt(event.target.dataset.index));
    }

    /***
     * Scrolls the slider to previous slide
     *
     * @returns void
     */
    prev() {
        const currentIndex = parseInt(this.getActiveSlide().dataset.index);
        this.scrollTo(currentIndex - 1);
    }

    /***
     * Scrolls the slider to the next slide
     *
     * @returns void
     */
    next() {
        const currentIndex = parseInt(this.getActiveSlide().dataset.index);
        this.scrollTo(currentIndex + 1);
    }

    /** Opens the lightbox
     *
     * @returns void
     */
    openLightbox() {
        this.lightbox.dialog.showModal();
        document.body.classList.add("noScroll");
        this.updateZoomButtons();
    }

    /** Closes the lightbox
     *
     * @returns void
     */
    closeLightbox() {
        this.lightbox.dialog.close();
    }
}